import React, { useState } from 'react';
import { InputAdornment } from "@material-ui/core";
import { Visibility, VisibilityOff } from "@material-ui/icons";
import ChatAPI from '../../chat-api';
import { useTheme, useChatState, useAppState } from '../../data';
import {
  Container, Input, Form, Content, SubmitButton, StyledIconButton, Title,
  OtherAuth, FormError, Loading, AuthError
} from './styles';

interface FormData {
  user: string;
  password: string;
}

const FormComponent: React.FC = () => {
  const { getTranslation } = useAppState();
  const { setUser, authsConfig, setIsLoggingIn, isLoggingIn, isExpanded } = useChatState();
  const { isAnonymous } = useAppState();
  const { backgroundColor, titleColor } = useTheme().header!;
  const { iconsPanel } = useTheme().footer!;
  const chatAPI = new ChatAPI();
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    user: '',
    password: ''
  });
  const [errors, setErrors] = useState<any>({});

  const handleChange = (key: keyof FormData) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [key]: e.target.value });
  };

  const handleSubmit = (e: any) => {
    e.preventDefault();
    setIsLoggingIn(true);
    chatAPI.signIn(formData)
      .then(res => {
        setUser(res);
        localStorage.setItem('explorer_user', JSON.stringify(res));
        window.postMessage(JSON.stringify({
          subject: '_authenticate',
          body: res
        }), window.location.origin);
        let _chat = localStorage.getItem('_chat');
        if (_chat) {
          try {
            _chat = JSON.parse(_chat);
            return chatAPI.getChat(_chat).catch(() => Promise.resolve());
          } catch (e) { }
        }
      }).catch(err => {
        if (err === 401) {
          setIsLoggingIn(false);
          errors.form = getTranslation('FORM_ERROR_CREDENTIALS');
          setErrors({ ...errors });
        }
        else {
          setIsLoggingIn(false);
          errors.form = getTranslation('FORM_ERROR_UNEXPECTED_ERROR');
          setErrors({ ...errors });
        }
      });
  }

  const getShowPassword = () => {
    return (
      <InputAdornment component="sdiv" position='end'>
        <StyledIconButton onClick={() => setShowPassword(prev => !prev)}>
          {!!formData.password ?
            showPassword ? <Visibility /> : <VisibilityOff />
            : <></>}
        </StyledIconButton>
      </InputAdornment>
    );
  }
  return (
    <Container
      as="sdiv"
      primary={backgroundColor}
      secondary={titleColor}
      text={iconsPanel?.iconsColor!}
      isExpanded={isExpanded}
    >
      {isExpanded && <Title as="sdiv">{getTranslation('FORM_LOGIN_MESSAGE')}</Title>}
      {authsConfig?.some(auth => auth?.type === 'basic') &&
        <Content as="sdiv">
          {errors.form && <FormError>{errors.form}</FormError>}
          <Form onSubmit={handleSubmit}>
            <Input
              classes={{ root: 'textFieldRoot' }}
              value={formData.user}
              onChange={handleChange('user')}
              InputLabelProps={{
                classes: {
                  focused: 'focusedLabel',
                }
              }}
              InputProps={{
                classes: {
                  root: 'inputRoot'
                }
              }}
              id='user'
              label={getTranslation('FORM_LABEL_USER')}
              size='small'
              required
            />
            <Input
              value={formData.password}
              onChange={handleChange('password')}
              InputLabelProps={{
                classes: {
                  focused: 'focusedLabel'
                }
              }}
              InputProps={{
                endAdornment: getShowPassword(),
                classes: {
                  root: 'inputRoot'
                }
              }}
              id='password'
              label={getTranslation('FORM_LABEL_PASSWORD')}
              size='small'
              required
              type={showPassword ? 'text' : 'password'}
            />
            <SubmitButton type='submit' disabled={isLoggingIn}>
              {isLoggingIn ? <Loading /> : getTranslation('FORM_SIGNIN_BUTTON')}
            </SubmitButton>
          </Form>
        </Content>}
      {authsConfig?.some(auth => auth?.type !== 'basic') &&
        <OtherAuth as="sdiv">{getTranslation('FORM_CUSTOM_AUTH_LABEL')} &nbsp;
          {authsConfig
            .filter(auth => auth.type !== 'basic')
            .map<React.ReactNode>(item => (
              <a href={`${item.loginUrl}?one_redirect=${encodeURIComponent(window.location.href)}`}>{item.name}</a>
            ))
            .reduce((prev, curr) => [prev, ` ${getTranslation('FORM_CUSTOM_AUTH_OR')} `, curr])
          }.
        </OtherAuth>
      }
      {(!isAnonymous && (!authsConfig || !authsConfig.length)) && <AuthError>{getTranslation('ERROR_NO_AUTHENTICATOR')}</AuthError>}
    </Container>
  );
}

export default React.memo(FormComponent);